import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { REACT_APP_API_URL } from "../../../../../env";
import {
  fetchUserDetails,
  rehydrateAuthState,
} from "../../../../../Redux/slice/features/authSlice";

const customStyles = `
  .revenue-wrapper {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    overflow: hidden;
    margin-top: 1.5rem;
    border: 1px solid #f1f5f9;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }
  .revenue-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: #ffffff;
    border-bottom: 1px solid #eaedf0;
  }
  .report-badge {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    padding: 8px 18px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.85rem;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
    letter-spacing: 0.02em;
  }
  .total-metrics {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .total-count {
    font-size: 0.9rem;
    color: #64748b;
    font-weight: 500;
  }
  .total-count span {
    font-weight: 700;
    color: #0f172a;
  }
  .revenue-table-container {
    overflow-x: auto;
    width: 100%;
  }
  .revenue-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 950px;
    text-align: left;
  }
  .revenue-table th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 18px 24px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }
  .revenue-table td {
    padding: 18px 24px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    font-size: 0.875rem;
    font-weight: 500;
  }
  .revenue-table tbody tr {
    transition: all 0.2s ease-in-out;
  }
  .revenue-table tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  }
  .revenue-table tbody tr:last-child td {
    border-bottom: none;
  }
  .val-badge {
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 0.02em;
  }
  .badge-payment {
    background: #e0e7ff;
    color: #4338ca;
  }
  .badge-amount {
    background: #d1fae5;
    color: #059669;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 8px 14px;
    display: inline-block;
  }
  .badge-date {
    background: #f1f5f9;
    color: #475569;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;
  }
  .receipt-id {
    font-family: monospace;
    color: #64748b;
    font-size: 0.85rem;
    background: #f8fafc;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
  }
  .email-td {
    color: #3b82f6;
    font-weight: 500;
  }
  .pagination-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: #f8fafc;
    border-top: 1px solid #eaedf0;
  }
  .page-info {
    font-size: 0.875rem;
    color: #475569;
    font-weight: 500;
  }
  .page-info span {
    font-weight: 700;
    color: #0f172a;
  }
  .page-controls {
    display: flex;
    gap: 12px;
  }
  .btn-page {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: #334155;
    padding: 8px 18px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  .btn-page:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #0f172a;
  }
  .btn-page:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    background: #f8fafc;
  }
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #94a3b8;
  }
  .empty-state-icon {
    font-size: 3rem;
    margin-bottom: 16px;
    color: #cbd5e1;
  }
`;

const TodayBookingRevenueTable = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentPage, itemsPerPage } = useSelector(
    (state) => state.pagination
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const userHotelId = user?.user?.PropertyID;

  useEffect(() => {
    dispatch(rehydrateAuthState());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const formatDate = useCallback((value, withTime = false) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return withTime
      ? date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  }, []);

  const fetchReport = useCallback(async () => {
    const HoteId = userHotelId || 1;
    const today = new Date().toISOString().split("T")[0];
    setLoading(true);

    try {
      const res = await axios.get(
        `${REACT_APP_API_URL}api/dashboard/admin/today-bookings-detailed-report`,
        {
          params: {
            HoteId: HoteId,
            fromDate: today,
            toDate: today,
          },
        }
      );

      const responseData = res.data?.data || res.data || [];
      const dataArray = Array.isArray(responseData) ? responseData : [];

      const formatted = dataArray.map((item) => ({
        PaymentID: item.PaymentID || "-",
        ReceiptNo: item.Notes_ReciptNo || "-",
        CustomerEmail: item.Notes_CustomerEmail || "-",
        CheckInDate: formatDate(item.Notes_CheckIn),
        CheckOutDate: formatDate(item.Notes_CheckOut),
        Amount: `₹${(Number(item.Amount || 0)).toLocaleString("en-IN", {})}`,
        Hotel: item.Notes_HotelName || "-",
      }));

      setData(formatted);
    } catch (err) {
      console.error("Error fetching report:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [formatDate, userHotelId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  }, [data, currentPage, itemsPerPage]);

  const handleNextPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage + 1));
  }, [dispatch, currentPage]);

  const handlePreviousPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage - 1));
  }, [dispatch, currentPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));

  return (
    <>
      <style>{customStyles}</style>
      <div className="revenue-wrapper">
        <div className="revenue-top-bar">
          <div className="report-badge">Today's Transactions</div>
          <div className="total-metrics">
            <div className="total-count">
              Total Records: <span>{data.length}</span>
            </div>
          </div>
        </div>

        <div className="revenue-table-container">
          <table className="revenue-table">
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Receipt No.</th>
                <th>Customer Email</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Total Amount</th>
                <th>Hotel Name</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    Loading revenue data...
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <span className="val-badge badge-payment">
                        {item.PaymentID}
                      </span>
                    </td>
                    <td>
                      <span className="receipt-id">{item.ReceiptNo}</span>
                    </td>
                    <td className="email-td">{item.CustomerEmail}</td>
                    <td>
                      <span className="badge-date">{item.CheckInDate}</span>
                    </td>
                    <td>
                      <span className="badge-date">{item.CheckOutDate}</span>
                    </td>
                    <td>
                      <span className="val-badge badge-amount">
                        {item.Amount}
                      </span>
                    </td>
                    <td>{item.Hotel}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="empty-state">
                      <div className="empty-state-icon">📄</div>
                      <div>No revenue records found for today.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-box">
          <div className="page-info">
            Page <span>{currentPage}</span> of <span>{totalPages}</span>
          </div>
          <div className="page-controls">
            <button
              className="btn-page"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="btn-page"
              onClick={handleNextPage}
              disabled={currentPage === totalPages || currentItems.length === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TodayBookingRevenueTable;
